import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { BookmarksSimple, Plus, Check, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * Small button/popover that lets the signed-in user add an item to one of
 * their collections. Supports both articles and author-works.
 */
export default function AddToCollectionButton({ itemType, itemId, size = 'sm' }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open && user) loadCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadCollections = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/collections/me`,
        { withCredentials: true }
      );
      setCollections(res.data);
    } catch (e) {
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const addToCollection = async (colId) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/collections/${colId}/items`,
        { item_type: itemType, item_id: itemId },
        { withCredentials: true }
      );
      toast.success('Added to collection');
      setOpen(false);
      loadCollections();
    } catch (e) {
      toast.error('Failed to add');
    }
  };

  const createAndAdd = async () => {
    if (!newName.trim()) return;
    try {
      setCreating(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/collections`,
        { name: newName.trim(), is_public: true },
        { withCredentials: true }
      );
      setNewName('');
      await addToCollection(res.data.collection_id);
    } catch (e) {
      toast.error('Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const isInCollection = (col) =>
    (col.items || []).some((it) => it.item_id === itemId && it.item_type === itemType);

  if (!user) {
    return (
      <button
        onClick={() => toast.error('Please sign in to save to a collection')}
        data-testid="add-to-collection-signin"
        className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full py-1.5 px-3 font-semibold hover:bg-primary/20"
      >
        <BookmarksSimple size={14} /> Save
      </button>
    );
  }

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`add-to-collection-btn-${itemId}`}
        className={`inline-flex items-center gap-1 ${size === 'lg' ? 'text-sm py-2 px-4' : 'text-xs py-1.5 px-3'} bg-primary/10 text-primary rounded-full font-semibold hover:bg-primary/20`}
      >
        <BookmarksSimple size={size === 'lg' ? 16 : 14} /> Save to collection
      </button>

      {open && (
        <div
          className="absolute z-50 mt-2 right-0 w-72 bg-card border border-border rounded-lg shadow-lg p-3"
          data-testid="collection-picker"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold">Save to collection</p>
            <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
              <X size={14} />
            </button>
          </div>

          {loading ? (
            <p className="text-xs text-muted-foreground py-2">Loading…</p>
          ) : collections.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">No collections yet. Create one below.</p>
          ) : (
            <div className="max-h-52 overflow-y-auto space-y-1 mb-2">
              {collections.map((col) => {
                const inIt = isInCollection(col);
                return (
                  <button
                    key={col.collection_id}
                    onClick={() => !inIt && addToCollection(col.collection_id)}
                    disabled={inIt}
                    data-testid={`col-option-${col.collection_id}`}
                    className={`w-full text-left px-2 py-2 rounded flex items-center justify-between gap-2 ${inIt ? 'bg-primary/5 text-muted-foreground cursor-default' : 'hover:bg-muted'}`}
                  >
                    <span className="text-sm truncate">{col.name}</span>
                    {inIt ? <Check size={14} className="text-primary shrink-0" /> : <Plus size={14} className="shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}

          <div className="border-t border-border pt-2 flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New collection name"
              data-testid="new-collection-input"
              className="flex-1 text-sm px-2 py-1.5 bg-background border border-border rounded outline-none focus:border-primary"
            />
            <button
              onClick={createAndAdd}
              disabled={!newName.trim() || creating}
              data-testid="create-collection-btn"
              className="text-xs bg-primary text-primary-foreground rounded px-3 font-semibold disabled:opacity-50"
            >
              {creating ? '…' : 'Create'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
